import { db, Timestamp } from '@/firebase.js'
import { firestoreAction } from 'vuexfire'
import { values, hasIn } from 'lodash'
import {
  newId,
  isDateBySecondsToday,
  isDateBySecondsInFutureByDays,
  isDateBySecondsInPastByDays,
  format,
  percentChange
} from '@/utils'

import { DateTime } from 'luxon'
import { cloneDeep } from 'lodash'

const getDefaultState = () => {
  return {
    data: [],
    ref: ''
  }
}

const state = getDefaultState()

const getters = {
  data(state) {
    return { ...state.data }
  },
  ref(state) {
    return state.ref
  },
  invoicesToday(state, getters) {
    try {
      let list = []
      getters['invoicesOrdered'].forEach(invoice => {
        try {
          let deliveryDate =
            getters['deliveryDateByInvoiceId'](invoice.id) ||
            getters['orderDateByInvoiceId'](invoice.id)
          //console.log(deliveryDate, invoice)
          if (
            Object.prototype.hasOwnProperty.call(deliveryDate, 'seconds') &&
            isDateBySecondsToday(deliveryDate.seconds)
          )
            list.push(invoice.id)
        } catch (e) {
          console.error(e, invoice)
        }
      })
      return list
    } catch (e) {
      console.error(e)
      return null
    }
  },
  invoicesPastIncomplete(state, getters) {
    try {
      let list = []
      getters['invoicesOrdered'].forEach(invoice => {
        try {
          let deliveryDate =
            getters['deliveryDateByInvoiceId'](invoice.id) ||
            getters['orderDateByInvoiceId'](invoice.id)
          //console.log(deliveryDate, invoice)
          if (
            Object.prototype.hasOwnProperty.call(deliveryDate, 'seconds') &&
            isDateBySecondsInPastByDays(deliveryDate.seconds, 6) &&
            invoice.completed === false
          )
            list.push(invoice.id)
        } catch (e) {
          console.error(e, invoice)
        }
      })
      return list
    } catch (e) {
      console.log(e)
      return null
    }
  },
  invoicesFuture(state, getters) {
    try {
      let list = []
      getters['invoicesOrdered'].forEach(invoice => {
        try {
          let deliveryDate =
            getters['deliveryDateByInvoiceId'](invoice.id) ||
            getters['orderDateByInvoiceId'](invoice.id)
          //console.log(deliveryDate, invoice)
          if (
            Object.prototype.hasOwnProperty.call(deliveryDate, 'seconds') &&
            isDateBySecondsInFutureByDays(deliveryDate.seconds, 6)
          )
            list.push(invoice.id)
        } catch (e) {
          console.error(e, invoice)
        }
      })
      return list
    } catch (e) {
      console.log(e)
      return null
    }
  },
  invoices(state) {
    return Object.assign({}, ...state.data.map(i => ({ [i.id]: i })))
  },
  invoicesOrdered(state, getters) {
    let eitherDate = function (d) {
      return dateFix(d.deliveryDate) || dateFix(d.orderDate)
    }
    let dateFix = function (d) {
      if (d === null) return false
      return toDate(d) || Object.prototype.hasOwnProperty.call(d, 'seconds')
        ? new Date(d.seconds * 1000)
        : d === ''
          ? false
          : false // eventually remove
    }
    let toDate = function (d) {
      try {
        return d.toDate()
      } catch {
        return d instanceof Date && !isNaN(d) // isValidDate
      }
    }
    return [
      ...Object.values(getters['invoices']).sort(
        (a, b) => eitherDate(b) - eitherDate(a)
      )
    ]
  },
  invoiceById(state) {
    return id => {
      return state.data.find(doc => doc.id === id)
    }
  },
  isInvoiceCompletedByInvoiceId(state, getters) {
    return id => {
      try {
        return getters['invoiceById'](id).completed
      } catch {
        return null
      }
    }
  },
  vendorIdByInvoiceId(state, getters) {
    return id => {
      try {
        return getters['invoiceById'](id).vendorId
      } catch {
        return null
      }
    }
  },
  isInvoice(state, getters) {
    return id => {
      try {
        return Object.keys(getters.data).some(k => getters.data[k].id === id)
      } catch {
        return false
      }
    }
  },
  isInvoiceActive(state, getters) {
    return id => {
      try {
        if (
          !Object.prototype.hasOwnProperty.call(
            getters.productById(id),
            'active'
          )
        )
          return true
        return getters.productById(id).active ? true : false
      } catch {
        return false
      }
    }
  },
  photoURLbyImageId(state, getters) {
    return id => {
      try {
        let invoiceId = Object.keys(getters['invoices']).find(
          key => getters['invoices'][key].imageId === id
        )
        let downloadURL = getters['invoiceById'](invoiceId).downloadURL || null
        return downloadURL
      } catch {
        return null
      }
    }
  },
  deliveryDateByInvoiceId(state, getters) {
    return id => {
      try {
        return (
          getters['invoiceById'](id)['deliveryDate'] ||
          getters['invoiceById'](id)['orderDate']
        )
      } catch {
        return null
      }
    }
  },
  orderDateByInvoiceId(state, getters) {
    return id => {
      try {
        return getters['invoiceById'](id)['orderDate']
      } catch {
        return null
      }
    }
  },
  lastInvoiceByVendor(state) {
    return vendorId => {
      try {
        return state.data.find(i => i.vendorId == vendorId)
      } catch {
        return null
      }
    }
  },
  //
  // input: invoiceId<str>
  // ouput: [productId<str>, productId<str>...]
  productsByInvoiceId(state, getters) {
    return invoiceId =>
      values(getters['invoiceById'](invoiceId))
        .filter(value => hasIn(value, 'productId'))
        .map(invoiceItem => invoiceItem.productId)
  },
  productsZeroedOutByInvoiceId(state, getters) {
    return invoiceId =>
      values(getters['invoiceById'](invoiceId))
        .filter(value => hasIn(value, 'productId'))
        .filter(value => {
          if (
            hasIn(value, 'amountOrdered') &&
            (value.amountOrdered === false || value.amountOrdered === 0) &&
            !hasIn(value, 'action')
          )
            return true
        })
        .map(invoiceItem => invoiceItem.productId)
  },
  productsCosts(state, getters) {
    //  output: {
    //    productId<str>: [{
    //      cost, date, invoiceId, productId
    //    }...]
    //  }
    let products = {}

    values(getters['invoices'])
      .filter(invoice => hasIn(invoice, 'completed') && invoice.completed)
      .forEach(invoice =>
        values(invoice)
          .filter(value => hasIn(value, 'productId'))
          .forEach(invoiceItem => {
            if (!hasIn(products, invoiceItem.productId))
              products[invoiceItem.productId] = []
            invoiceItem.cost &&
              products[invoiceItem.productId].push({
                cost: invoiceItem.cost,
                productId: invoiceItem.id,
                invoiceId: invoice.id,
                date: invoice.date
              })
          })
      )

    return products
  },
  productByInvoiceIdByProductId(state, getters) {
    return invoiceId => {
      return productId => {
        return values(getters['invoiceById'](invoiceId)).filter(
          value => hasIn(value, 'productId') && value.productId === productId
        )[0]
      }
    }
  },
  productCostsByInvoiceById(state, getters) {
    return id => {
      if (getters['isInvoice'](id)) {
        try {
          return Object.values(getters['invoiceById'](id))
            .filter(item => {
              if (typeof item === 'object' && item !== null)
                return (
                  Object.prototype.hasOwnProperty.call(item, 'cost') &&
                  !isNaN(item.cost) &&
                  typeof item.cost === 'number' &&
                  item.cost > 0
                )
              else return false
            })
            .map(item => {
              return { id: item.productId, cost: item.cost }
            })
        } catch (e) {
          console.log("Error getting invoice's product prices.", id, e)
        }
      }
      return []
    }
  },
  lastInvoiceByVendorWithProduct(state) {
    return vendorId => {
      return productId => {
        try {
          return state.data.find(i => {
            if (i.vendorId == vendorId) {
              try {
                return i[productId]['cost']
              } catch {
                return false
              }
            }
            return false
          })
        } catch {
          return null
        }
      }
    }
  },
  invoiceCsvByInvoiceId(state, getters, rootState, rootGetters) {
    return invoiceId => {
      let formatCsv = amount => format(amount).replace(',', '')

      // check this invoiceId is a valid invoice
      if (!getters['invoices'][invoiceId])
        throw new Error(
          'Invalid InvoiceId: ',
          invoiceId ? invoiceId : 'No ID Provided'
        )

      // get invoice data - getters['invoiceById'](invoiceId)
      let invoice = { ...getters['invoiceById'](invoiceId) }

      let productRows = [
        [
          'Product',
          'Count',
          'Ordered',
          'Received',
          'Returned',
          'Billed',
          'Action',
          'Return Reason',
          'Item Cost',
          'Total Cost'
        ]
      ]

      getters['productsByInvoiceId'](invoiceId).forEach(productId => {
        let product = { ...invoice[productId] }
        let received = product.amountReceived || 0
        let returned = product.returnQty || product.amountReturned || 0
        let billed = product.amountBilled || 0
        let action = product.action || ''

        if (action == 'Discrepancy: Wrong Number Received') {
          returned = '-'
        } else if (action == 'Received') {
          returned = '-'
          billed = '-'
        } else if (action.slice(0, 8) == 'Returned') {
          billed = '-'
        } else if (action == '') {
          returned = '-'
          billed = '-'
          received = '-'
        }

        productRows.push([
          `"${product.name || ''}"`,
          `"${product.amountEntered}"`,
          `"${product.amountOrdered}"`,
          `"${received}"`,
          `"${returned}"`,
          `"${billed}"`,
          `"${action || '-'}"`,
          `"${product.returnReason || '-'}"`,
          `"${formatCsv(product.cost) || '-'}"`,
          `"${formatCsv(product.totalCost) || '-'}"`
        ])
      })

      productRows.push(['', '', '', '', '', '', '', '', '', ''])
      productRows.push([
        'Fees',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        `${formatCsv(invoice.fees)}`
      ])
      productRows.push([
        'Tax',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        `${formatCsv(invoice.tax)}`
      ])
      productRows.push([
        'Total',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        `${formatCsv(invoice.totalCost)}`
      ])

      // join data
      let outputCsv = ''
      productRows.forEach(row => {
        outputCsv += row.join(',')
        outputCsv += '\n'
      })

      let filename = 'invoice'
      let seconds =
        (invoice.invoiceDate && invoice.invoiceDate.seconds) ||
        (invoice.deliveryDate && invoice.deliveryDate.seconds) ||
        (invoice.orderDate && invoice.orderDate.seconds)
      seconds = seconds
        ? DateTime.fromSeconds(seconds).toFormat('yyyyMMdd')
        : ''

      try {
        filename = `${seconds}_invoice_${invoice.invoiceNumber ? invoice.invoiceNumber + '_' : ''
          }${rootGetters['storeModule/vendors/vendorName'](invoice.vendorId)}.csv`
      } catch (e) {
        console.log(e)
      }

      // return object
      return { filename, data: outputCsv }
    }
  },
  daysSinceLastInvoiceCreated(state, getters) {
    try {
      const invoices = getters['invoicesOrdered']
      if (!invoices || invoices.length === 0) return null

      const mostRecentInvoice = invoices[0]
      if (!mostRecentInvoice.orderDate) return null

      const orderDate = DateTime.fromSeconds(mostRecentInvoice.orderDate.seconds)
      const now = DateTime.now()

      console.log('Last order date:', orderDate.toISO())
      console.log('Current time:', now.toISO())
      console.log('Diff in days:', now.diff(orderDate, 'days').days)

      return Math.floor(now.diff(orderDate, 'days').days)
    } catch (e) {
      console.error('Error calculating days since last invoice:', e)
      return null
    }
  }
}

const actions = {
  bindStore: firestoreAction(
    ({ bindFirestoreRef, commit, state }, { storeId }) => {
      commit(
        'setRef',
        db
          .collection('stores')
          .doc(storeId)
          .collection('invoices')
      )
      return bindFirestoreRef(
        'data',
        state.ref
          .where(
            'date',
            '>=',
            DateTime.now()
              .minus({ days: 42 })
              .toJSDate()
          )
          .where('status', 'in', ['saved', 'open', 'resend', 'ordered'])
      )
    }
  ),
  unbindStore: firestoreAction(({ unbindFirestoreRef }) => {
    unbindFirestoreRef('data')
  }),
  createInvoice: async function ({ getters }, payload) {
    // when invoice doc is created in firestore,
    // server sends the email per email object
    let id = newId()
    let data = {
      ...payload,
      date: Timestamp.now(),
      createdAt: Timestamp.now(),
      status: 'open',
      completed: false,
      tax: 0,
      fees: 0,
      totalCost: 0,
      invoiceNumber: null,
      completedAt: null,
      completedBy: null,
      id
    }
    console.log('invoices/createInvoice', id, data)
    return getters.ref
      .doc(id)
      .set(data, { merge: true })
      .then(() => {
        return id
      })
      .catch(err => {
        return err
      })
  },
  createInvoiceByVendorId({ dispatch, rootGetters }, vendorId) {
    console.log('storeModule/invoices/createInvoiceByVendorId', vendorId)
    if (!vendorId || !rootGetters['storeModule/vendors/isVendorId'](vendorId))
      throw `Invalid vendorId: ${vendorId}`

    let invoice = {
      vendorId,
      // invoiceId: ( added by dispatch('createInvoice) )
      orderId: null,
      orderDate: Timestamp.now().toDate(),
      deliveryDate: Timestamp.now().toDate(), //getters['nextDeliveryDate'](vendorId)
      vendorName: rootGetters['storeModule/vendors/vendorName'](vendorId)
    }
    return dispatch('createInvoice', invoice)
  },

  closeInvoice({ dispatch, rootGetters }, id) {
    if (!id) return

    // update invoice status to saved
    dispatch('updateItem', {
      id,
      data: {
        status: 'saved',
        completedAt: Timestamp.now(),
        date: Timestamp.now(),
        completed: true
      }
    })

    // prepare product prices & check for notification thresholds
    let invoice = rootGetters['storeModule/productPricesByInvoiceId'](id)
    let productPricesClone = cloneDeep(rootGetters['storeModule/productPrices'])
    let thresholdPercent = rootGetters['storeModule/notificationsProductPriceChangePercent']
    let notifications = []

    invoice.forEach(item => {
      if (
        productPricesClone[item.id] &&
        productPricesClone[item.id].cost &&
        item.cost &&
        typeof item.cost === 'number' &&
        typeof productPricesClone[item.id].cost === 'number' &&
        productPricesClone[item.id].cost !== item.cost
      ) {
        let changePercent = percentChange(
          productPricesClone[item.id].cost,
          item.cost
        )

        if (
          changePercent >= thresholdPercent
        ) {
          notifications.push({
            productId: item.id,
            productName: rootGetters['storeModule/products/productName'](
              item.id
            ),
            priceOld: productPricesClone[item.id].cost,
            priceNew: item.cost,
            percentChange: changePercent
          })
        }
      }

      productPricesClone[item.id] = {
        ...item,
        lastChangedOn: Timestamp.now()
      }
    })

    // send notifications
    if (notifications.length > 0)
      dispatch(
        'storeModule/createProductPriceChangesNotification',
        notifications,
        { root: true }
      )

    // update product prices
    if (invoice.length > 0)
      dispatch(
        'storeModule/updateItem',
        { productPrices: productPricesClone },
        { root: true }
      )

    return
  },

  resendEmail: async function ({ dispatch }, id) {
    console.log('resending email...')
    if (!id) throw new Error('No ID provided.')
    if (!getters.isInvoice(id)) throw new Error('Invalid ID.')
    return dispatch('updateItem', { id, data: { status: 'resend' } })
  },
  delete: async function ({ dispatch }, id) {
    console.log('invoices/delete', id)
    if (!id) throw new Error('No ID provided.')
    return dispatch('updateItem', { id, data: { status: 'deleted' } })
  },
  updateItem({ getters }, { id, data }) {
    console.log('invoices/updateItem', id, data)
    return getters.ref.doc(id).update(data)
  },
  addProductToInvoice(
    { getters, rootGetters, dispatch },
    { invoiceId, productId }
  ) {
    console.log('storeModule/invoices/addProductToInvoice', {
      invoiceId,
      productId
    })

    let list = [...getters['productsZeroedOutByInvoiceId'](invoiceId)]

    if (list.includes(productId))
      return dispatch('updateItem', {
        id: invoiceId,
        data: { [`${productId}.action`]: 'Received' }
      })
    else {
      let data = {
        [productId]: {
          amountOrdered: false,
          name: rootGetters['storeModule/products/productName'](productId),
          productId,
          amountEntered: 0,
          invoiceAction: null,
          returnQty: 0,
          returnReason: null,
          cost: 0,
          totalCost: 0
        }
      }
      if (
        rootGetters['storeModule/invoices/vendorIdByInvoiceId'](invoiceId) ==
        rootGetters['storeModule/products/vendorIdByProductId'](productId)
      )
        return dispatch('updateItem', { id: invoiceId, data })
      else {
        console.log({
          invoiceId,
          vendorIdByInvoiceId: rootGetters[
            'storeModule/invoices/vendorIdByInvoiceId'
          ](invoiceId),
          productId,
          vendorIdByProductId: rootGetters[
            'storeModule/products/vendorIdByProductId'
          ](productId)
        })
        throw Error("vendorId's do not match.")
      }
    }
  },
  updateAmountReceivedByInvoiceIdByItemId(
    { dispatch, getters },
    { invoiceId, productId, amountReceived }
  ) {
    console.log(
      'invoice/updateAmountReceivedByInvoiceIdByItemId',
      invoiceId,
      productId,
      amountReceived
    )
    if (
      !invoiceId ||
      !productId ||
      isNaN(amountReceived) ||
      typeof amountReceived !== 'number'
    )
      throw 'incorect data provided'

    let data = { [productId + '.amountReceived']: amountReceived }

    let invoiceProduct = getters['invoiceById'](invoiceId)[productId]
    if (Object.prototype.hasOwnProperty.call(invoiceProduct, 'cost')) {
      data[productId + '.totalCost'] = amountReceived * invoiceProduct.cost
    }

    return dispatch('updateItem', {
      id: invoiceId,
      data
    }).then(() => {
      dispatch('fixTotalCostByInvoiceId', { invoiceId })
    })
  },
  updateAmountBilledByInvoiceIdByItemId(
    { dispatch, getters },
    { invoiceId, productId, amountBilled }
  ) {
    console.log(
      'invoice/updateAmountBilledByInvoiceIdByItemId',
      invoiceId,
      productId,
      amountBilled
    )
    if (
      !invoiceId ||
      !productId ||
      isNaN(amountBilled) ||
      typeof amountBilled !== 'number'
    )
      throw 'incorect data provided'

    let data = { [productId + '.amountBilled']: amountBilled }

    let invoiceProduct = getters['invoiceById'](invoiceId)[productId]
    if (Object.prototype.hasOwnProperty.call(invoiceProduct, 'cost')) {
      data[productId + '.totalCost'] = amountBilled * invoiceProduct.cost
    }

    return dispatch('updateItem', {
      id: invoiceId,
      data
    }).then(() => {
      dispatch('fixTotalCostByInvoiceId', { invoiceId })
    })
  },
  updateCostByInvoiceIdByItemId(
    { dispatch, getters },
    { invoiceId, productId, cost }
  ) {
    console.log(
      'invoices/updateAmountReceivedByInvoiceIdByItemId',
      invoiceId,
      productId,
      cost
    )
    if (!invoiceId || !productId || isNaN(cost) || typeof cost !== 'number')
      throw 'incorect data provided'

    let data = { [productId + '.cost']: cost }

    let invoiceProduct = getters['invoiceById'](invoiceId)[productId]
    if (
      Object.prototype.hasOwnProperty.call(invoiceProduct, 'amountReceived')
    ) {
      data[productId + '.totalCost'] = invoiceProduct.amountReceived * cost
    }
    return dispatch('updateItem', {
      id: invoiceId,
      data
    }).then(() => {
      dispatch('fixTotalCostByInvoiceId', { invoiceId })
    })
  },
  updateActionByInvoiceIdByItemId(
    { dispatch },
    { invoiceId, productId, action }
  ) {
    console.log(
      'invoices/updateActionByInvoiceIdByItemId',
      invoiceId,
      productId,
      action
    )
    if (!invoiceId || !productId || !action) throw 'incorect data provided'
    let data = { [productId + '.action']: action }

    return dispatch('updateItem', {
      id: invoiceId,
      data
    })
  },
  updateReturnReasonByInvoiceIdByItemId(
    { dispatch },
    { invoiceId, productId, returnReason }
  ) {
    console.log(
      'invoices/updateReturnReasonByInvoiceIdByItemId',
      invoiceId,
      productId,
      returnReason
    )
    if (!invoiceId || !productId || !returnReason)
      throw 'incorect data provided'
    let data = { [productId + '.returnReason']: returnReason }

    return dispatch('updateItem', {
      id: invoiceId,
      data
    })
  },
  updateAmountReturnedByInvoiceIdByItemId(
    { dispatch },
    { invoiceId, productId, amountReturned }
  ) {
    console.log(
      'invoices/updateAmountReturnedByInvoiceIdByItemId',
      invoiceId,
      productId,
      amountReturned
    )
    if (
      !invoiceId ||
      !productId ||
      isNaN(amountReturned) ||
      typeof amountReturned !== 'number'
    )
      throw 'incorect data provided'

    let data = { [productId + '.amountReturned']: amountReturned }

    return dispatch('updateItem', {
      id: invoiceId,
      data
    })
  },
  updateFeesByInvoiceId({ dispatch }, { invoiceId, fees }) {
    console.log('invoices/updateFeesByInvoiceId', invoiceId, fees)
    if (!invoiceId || isNaN(fees) || typeof fees !== 'number')
      throw 'incorect data provided'

    let data = { fees }

    return dispatch('updateItem', {
      id: invoiceId,
      data
    }).then(() => {
      dispatch('fixTotalCostByInvoiceId', { invoiceId })
    })
  },
  updateTaxByInvoiceId({ dispatch }, { invoiceId, tax }) {
    console.log('invoices/updateFeesByInvoiceId', invoiceId, tax)
    if (!invoiceId || isNaN(tax) || typeof tax !== 'number')
      throw 'incorect data provided'

    let data = { tax }

    return dispatch('updateItem', {
      id: invoiceId,
      data
    }).then(() => {
      dispatch('fixTotalCostByInvoiceId', { invoiceId })
    })
  },
  updateInvoiceNumberByInvoiceId(
    { dispatch, getters },
    { invoiceId, invoiceNumber }
  ) {
    console.log(
      'invoices/updateInvoiceNumberByInvoiceId',
      invoiceId,
      invoiceNumber
    )
    if (!invoiceId || !invoiceNumber) throw 'incorrect data provided'

    if (
      getters['isInvoice'](invoiceId) &&
      getters['invoiceById'](invoiceId).invoiceNumber !== invoiceNumber
    )
      return dispatch('updateItem', { id: invoiceId, data: { invoiceNumber } })
    else throw "invalid invoiceId, or invoiceNumber hasn't changed"
  },
  toggleInvoiceCompletedByInvoiceId({ getters, dispatch }, invoiceId) {
    console.log('invoices/toggleInvoiceCompletedByInvoiceId', invoiceId)
    if (!invoiceId) throw 'incorrect data provided'
    if (getters['isInvoice'](invoiceId))
      return dispatch('updateItem', {
        id: invoiceId,
        data: {
          completed: !getters['isInvoiceCompletedByInvoiceId'](invoiceId)
        }
      })
    else throw 'invalid invoiceId'
  },
  fixTotalCostByInvoiceId({ getters, dispatch }, { invoiceId }) {
    console.log('invoices/fixTotalCostByInvoiceId', invoiceId)
    const invoice = getters['invoiceById'](invoiceId)

    // For scanned invoices, the scan header is the authoritative total (reflects vendor
    // credits/returns that aren't modeled as product line items in our system).
    // Staff-logged returns are internal records and must not alter the invoice total.
    const scanHeaderTotalCents =
      invoice.autoReconciliation &&
      invoice.autoReconciliation.header &&
      typeof invoice.autoReconciliation.header.totalAmountCents === 'number'
        ? invoice.autoReconciliation.header.totalAmountCents
        : null

    if (scanHeaderTotalCents !== null) {
      return dispatch('updateItem', {
        id: invoiceId,
        data: { totalCost: scanHeaderTotalCents / 100 }
      })
    }

    let totalCost = 0
    Object.values(invoice).forEach(item => {
      if (
        typeof item === 'object' &&
        item !== null &&
        Object.prototype.hasOwnProperty.call(item, 'productId') &&
        Object.prototype.hasOwnProperty.call(item, 'totalCost')
      ) {
        totalCost += item.totalCost
      }
    })
    totalCost += invoice.fees
    totalCost += invoice.tax
    return dispatch('updateItem', {
      id: invoiceId,
      data: { totalCost: totalCost }
    })
  },
  resetStoreState({ commit }) {
    commit('resetState', null)
  }
}

const mutations = {
  setRef(state, ref) {
    state.ref = ref
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  }
}

const invoices = {
  namespaced: true,

  state,
  getters,
  mutations,
  actions
}

export default invoices

// invoiceId: {
//   vendor: {
//     vendorId,
//     vendorName,
//     contactName

//     storeAccount
//     storeAddress

//     orderDate
//     deliveryDate
//   },

//   completedAt:
//   completedBy:
//   status:

//   productId: {
//     // productId
//     // orderCode
//     // name

//     // amountEntered
//     // amountOrdered

//     // inputType?
//     // par
//     // order unit (50 rolls)
//     // qty per order unit

//     // co stTotal (each/total?) (costEach = amoundOrdered - returnQty / total)
//     // invoiceAction
//     // returnReason
//     // returnQty
//   }
// }
